import React, { useState } from 'react';
import { TrendingUp, Award, Lightbulb, ChevronRight, X, ChevronDown, ChevronUp, CheckCircle2 } from 'lucide-react';
import { getLevelName } from '../utils/progressionCriteria';

const GuidancePanel = ({
    progressionStatus,
    suggestions,
    onAdvance,
    onSelectSuggestion,
    userSettings,
    onDismiss,
    embedded = false
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showRequirements, setShowRequirements] = useState(false);

    if (!progressionStatus && (!suggestions || suggestions.length === 0)) {
        return null;
    }

    const {
        canAdvance,
        nextLevel,
        overallProgress,
        requirements,
        method,
        criteria
    } = progressionStatus || {};

    const nextLevelName = nextLevel ? getLevelName(nextLevel) : '';
    const topSuggestions = suggestions?.slice(0, 3) || [];

    return (
        <div className={embedded ? "" : "bg-surface border-2 border-primary/20 rounded-2xl overflow-hidden shadow-xl transition-all duration-300"}>
            {/* Header */}
            <div className={`p-4 flex items-center justify-between transition-colors ${embedded ? "bg-base/30 rounded-xl" : "bg-surface-hover/30 border-b border-border"}`}>
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg text-primary">
                        <TrendingUp size={20} />
                    </div>
                    <div>
                        <h2 className="font-bold text-main leading-none mb-1">Your Path to Mastery</h2>
                        <p className="text-[10px] text-muted uppercase tracking-wider font-bold">Personalized Guidance</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="p-1.5 hover:bg-surface rounded-lg text-muted hover:text-primary transition-all"
                    >
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    {onDismiss && (
                        <button
                            onClick={onDismiss}
                            className="p-1.5 hover:bg-surface rounded-lg text-muted hover:text-error transition-all"
                        >
                            <X size={20} />
                        </button>
                    )}
                </div>
            </div>

            {isExpanded && (
                <div className={`${embedded ? "pt-4 pb-2" : "p-4"} space-y-6`}>
                    {/* Progression Section */}
                    {progressionStatus && (
                        <div className="space-y-4">
                            {canAdvance ? (
                                <div className="bg-success/10 border border-success/30 rounded-xl p-4 animate-in fade-in zoom-in-95 duration-300">
                                    <div className="flex items-start gap-3">
                                        <Award className="text-success flex-shrink-0" size={24} />
                                        <div className="flex-1">
                                            <h3 className="text-success font-bold mb-1">Ready to Level Up!</h3>
                                            <p className="text-xs text-main mb-3">
                                                {method === 'primary'
                                                    ? "You've mastered the Koch lessons for this stage."
                                                    : `Requirements met for ${criteria}.`}
                                            </p>
                                            <button
                                                onClick={onAdvance}
                                                className="w-full flex items-center justify-center gap-2 bg-success hover:bg-success/80 text-success-content font-bold py-2 rounded-lg transition-all shadow-lg"
                                            >
                                                Advance to Level {nextLevel}: {nextLevelName}
                                                <ChevronRight size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <div className="flex justify-between items-end">
                                        <div className="text-sm font-bold text-main">
                                            Progress to Level {nextLevel}: <span className="text-primary">{nextLevelName}</span>
                                        </div>
                                        <div className="text-xs font-bold text-primary">{overallProgress}%</div>
                                    </div>
                                    <div className="h-2.5 bg-base rounded-full overflow-hidden border border-border">
                                        <div
                                            className="h-full bg-gradient-to-r from-primary/50 to-primary transition-all duration-1000 ease-out shadow-[0_0_10px_rgba(var(--color-primary-rgb),0.5)]"
                                            style={{ width: `${overallProgress}%` }}
                                        />
                                    </div>

                                    <button
                                        onClick={() => setShowRequirements(!showRequirements)}
                                        className="flex items-center gap-1 text-[10px] font-bold text-muted hover:text-primary transition-colors uppercase tracking-widest"
                                    >
                                        {showRequirements ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                        {showRequirements ? 'Hide' : 'View'} Detailed Requirements
                                    </button>

                                    {showRequirements && requirements && (
                                        <div className="pt-2 space-y-2 animate-in fade-in slide-in-from-top-2">
                                            {requirements.map((req, idx) => (
                                                <div key={idx} className="space-y-1">
                                                    <div className="flex items-center justify-between text-xs">
                                                        <div className="flex items-center gap-2">
                                                            {req.met ? (
                                                                <CheckCircle2 size={12} className="text-success" />
                                                            ) : (
                                                                <div className="w-3 h-3 rounded-full border border-muted/50" />
                                                            )}
                                                            <span className={req.met ? 'text-success font-medium' : 'text-main'}>
                                                                {req.description}
                                                            </span>
                                                        </div>
                                                        {req.count !== undefined && (
                                                            <span className="text-primary font-bold">
                                                                {req.count} / {req.threshold || req.count}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {!req.met && req.progress !== undefined && (
                                                        <div className="h-1 bg-base rounded-full overflow-hidden ml-5">
                                                            <div
                                                                className="h-full bg-primary/40"
                                                                style={{ width: `${req.progress}%` }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Suggestions Section */}
                    {topSuggestions.length > 0 && (
                        <div className="space-y-3">
                            <h3 className="text-xs font-bold text-muted uppercase tracking-wider flex items-center gap-2">
                                <Lightbulb size={14} className="text-warning" />
                                Recommended Training
                            </h3>
                            <div className="grid gap-2">
                                {topSuggestions.map((suggestion) => (
                                    <button
                                        key={suggestion.id}
                                        onClick={() => onSelectSuggestion(suggestion)}
                                        className="group w-full flex items-center gap-3 p-3 bg-base hover:bg-surface-hover border border-border hover:border-primary/50 rounded-xl transition-all duration-200 text-left"
                                    >
                                        <div className="text-2xl bg-surface p-2 rounded-lg group-hover:scale-110 transition-transform">
                                            {suggestion.icon}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-sm text-main group-hover:text-primary transition-colors">
                                                {suggestion.title}
                                            </div>
                                            <div className="text-[11px] text-muted line-clamp-1">
                                                {suggestion.description}
                                            </div>
                                        </div>
                                        <div className="p-1 bg-primary/10 text-primary rounded opacity-0 group-hover:opacity-100 transition-opacity">
                                            <ChevronRight size={16} />
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default GuidancePanel;
